import React from 'react';
import PropTypes from 'prop-types';
import { Box, Tabs, Tab, Typography, CircularProgress, Chip } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GridOnIcon from '@mui/icons-material/GridOn';
import FunctionsIcon from '@mui/icons-material/Functions';
import useThemeStyles from '../../hooks/useThemeStyles';
import useGameDesign from './hooks/useGameDesign';
import ErdSection from './erd/ErdSection';
import TypeChartSection from './typechart/TypeChartSection';
import FormulasSection from './formulas/FormulasSection';

export const GAME_DESIGN_TOOLS = [
  { id: 'erd', label: 'ERD', icon: AccountTreeIcon },
  { id: 'types', label: 'Type Chart', icon: GridOnIcon },
  { id: 'formulas', label: 'Formulas', icon: FunctionsIcon },
];

const SAVE_LABELS = {
  saving: 'Saving…',
  saved: 'Saved',
  error: 'Save failed',
};

/**
 * Game design workspace: ERD, type chart and formulas, persisted per project.
 */
const GameDesignPage = ({ projectId, tool, onToolChange }) => {
  const { colors } = useThemeStyles();
  const { data, loading, saveState, updateSection } = useGameDesign(projectId);

  const activeTool = GAME_DESIGN_TOOLS.some((t) => t.id === tool) ? tool : 'erd';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: colors.interactive.primary }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        p: 3,
        backgroundColor: colors.background.primary,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ color: colors.text.primary, fontWeight: 600 }}>
          Game Design
        </Typography>
        {SAVE_LABELS[saveState] && (
          <Chip
            label={SAVE_LABELS[saveState]}
            size="small"
            sx={{
              backgroundColor: 'transparent',
              border: `1px solid ${
                saveState === 'error' ? colors.semantic.error : colors.border.subtle
              }`,
              color: saveState === 'error' ? colors.semantic.error : colors.text.secondary,
            }}
          />
        )}
      </Box>

      <Tabs
        value={activeTool}
        onChange={(_, value) => onToolChange && onToolChange(value)}
        sx={{
          mb: 3,
          borderBottom: `1px solid ${colors.border.subtle}`,
          '& .MuiTab-root': { color: colors.text.secondary, textTransform: 'none' },
          '& .Mui-selected': { color: colors.interactive.primary },
        }}
      >
        {GAME_DESIGN_TOOLS.map((item) => {
          const Icon = item.icon;
          return (
            <Tab
              key={item.id}
              value={item.id}
              label={item.label}
              icon={<Icon fontSize="small" />}
              iconPosition="start"
            />
          );
        })}
      </Tabs>

      {activeTool === 'erd' && (
        <ErdSection
          entities={data?.entities || []}
          relations={data?.relations || []}
          onEntitiesChange={(entities) => updateSection('entities', entities)}
          onRelationsChange={(relations) => updateSection('relations', relations)}
        />
      )}
      {activeTool === 'types' && (
        <TypeChartSection
          typeChart={data?.typeChart || { types: [], multipliers: [] }}
          onChange={(typeChart) => updateSection('typeChart', typeChart)}
        />
      )}
      {activeTool === 'formulas' && (
        <FormulasSection
          formulas={data?.formulas || []}
          onChange={(formulas) => updateSection('formulas', formulas)}
        />
      )}
    </Box>
  );
};

GameDesignPage.propTypes = {
  projectId: PropTypes.string.isRequired,
  tool: PropTypes.string,
  onToolChange: PropTypes.func,
};

GameDesignPage.defaultProps = {
  tool: 'erd',
  onToolChange: null,
};

export default GameDesignPage;
